import Axios from 'api/client';
import {
    Address,
    Auction,
    AuctionCategory,
    AuctionItem,
    Bid,
    BidStatus,
    Bidder,
    BidderInvoiceType,
    BidderPaymentPreview,
    Campaign,
    CampaignCustomField,
    CampaignExpert,
    CampaignItem,
    CampaignMatch,
    CampaignMemberInvite,
    CustomField,
    CustomFieldGroup,
    EventType,
    Member,
    PromoCode,
    PromoCodeType,
    Sponsor,
    SponsorItem,
    Team,
    Transaction,
} from 'types/domain';
import { CustomFieldType } from 'types/filter';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

// PARAMS
interface HasCampaignIdParam {
    campaignId: number;
}
interface HasAuctionIdParam {
    auctionId: number;
}
interface HasCampaignItemIdParam {
    itemId: number;
}

interface MediaUpload {
    id?: number;
    url: string;
    size: number;
    mime_type: string;
    collection?: string;
}

type CustomFieldRequest = {
    id?: number;
    title: string;
    description: string | null;
    group?: string | null;
    type: CustomFieldType;
    required: boolean;
    default_value?: string;
    answers: string[] | null;
};

interface TicketBundles {
    bundle_id: number;
    quantity_per_bundle: number;
}

interface ItemRequest extends Omit<Partial<AuctionItem>, 'pictures'> {
    pictures?: MediaUpload[];
    custom_fields?: CustomFieldRequest[];
    bundle_only?: boolean;
    bundles?: TicketBundles[];
    scope?: string | null;
}
interface HasItemRequest {
    data: ItemRequest;
}

interface CampaignItemBundleItemRequest {
    id: number;
    quantity_per_bundle: number;
}

// CAMPAIGNS
interface FetchCampaignsParams extends FilterParams {
    excl_type?: string;
}

interface UpdateCampaignParams<T> extends HasCampaignIdParam {
    data: T;
}

interface CampaignMatchRequestParams {
    name: string;
    description: string | null;
    enabled: boolean;
    amount: number;
    type: CampaignMatch['type'];
    start_at: string;
    end_at: string | null;
    show_public_amount: boolean;
    show_public_countdown: boolean;
    match_offline_donations: boolean;
    include_in_raised_amount: boolean;
}

// RESOURCES
interface FetchCampaignResourceParams extends FilterParams {}

// ITEMS
interface FetchCampaignItemsParams extends FilterParams, HasCampaignIdParam {
    auction_id?: number;
    is_auction?: boolean;
    is_bundle?: boolean;
    type?: 'ticket' | 'product';
}
interface FetchCampaignItemParams extends HasCampaignIdParam, HasCampaignItemIdParam {
    include?: string;
}
interface CreateCampaignItemParams extends HasCampaignIdParam, HasItemRequest {
    type?: 'ticket' | 'product' | 'bundle';
    subtype?: 'bundle' | 'hybrid' | 'physical' | null;
    bundle_only?: boolean;
}
interface UpdateCampaignItemParams
    extends HasCampaignIdParam,
        HasCampaignItemIdParam,
        HasItemRequest {}
interface CancelCampaignItemParams extends HasCampaignIdParam, HasCampaignItemIdParam {}
interface DeleteCampaignItemParams extends HasCampaignIdParam, HasCampaignItemIdParam {}
interface ReorderCampaignItemsParams {
    order: string;
}
interface ExtendCampaignItemParams extends HasCampaignIdParam, HasCampaignItemIdParam {
    end_at: string;
}

// PROMO CODES
interface FetchCampaignPromoCodesParams extends FilterParams {}

interface PromoCodeRequest {
    code: string;
    type: PromoCodeType;
    amount: number;
    uses: number | null;
    active: boolean;
    items: number[] | null;
}

// AUCTIONS
interface FetchAuctionParams extends HasCampaignIdParam, HasAuctionIdParam {}
interface CreateAuctionParams<T> extends HasCampaignIdParam {
    data: T;
}
interface UpdateAuctionParams<T> extends HasCampaignIdParam, HasAuctionIdParam {
    data: T;
}
interface DeleteAuctionParams extends HasCampaignIdParam, HasAuctionIdParam {}
interface CancelAuctionParams extends HasCampaignIdParam, HasAuctionIdParam {}

// BIDS
type BidStatusFilter = BidStatus | 'all_winners';
interface FetchCampaignItemBidsParams
    extends HasCampaignIdParam,
        HasCampaignItemIdParam,
        FilterParams {
    status?: BidStatusFilter;
}
interface FetchAuctionBidsParams extends HasCampaignIdParam, HasAuctionIdParam, FilterParams {
    status?: BidStatusFilter;
}

interface CreateCampaignItemBidBody {
    bidder_number: number;
    initial_amount: number;
    max_amount?: number;
    quantity?: number;
}

// BIDDERS
interface FetchAuctionBiddersParams extends FilterParams {}

interface CreateCampaignParams {
    title: string;
    type: EventType;
    enable_team_fundraising: boolean;
    enable_auction?: boolean;
}

interface CreateCampaignResponse {
    access: object;
    campaign: Campaign;
}

interface MarkUncollectableParams {
    secondChanceBidId?: number;
}

interface FetchCampaignExpertsParams {
    isAvailable?: boolean;
}

interface FetchCampaignInvitesParams extends FilterParams {
    filter?: 'pending';
}

// CAMPAIGNS
const createCampaign = (accountId: number, params: CreateCampaignParams) =>
    Axios.post<CreateCampaignResponse>('/campaigns', {
        account_id: accountId,
        ...params,
    }).then(response => response.data);

const fetchCampaigns = (accountId: number, params: FetchCampaignsParams = {}) => {
    const { filters, ...restParams } = params;
    return Axios.post<PaginatedData<Campaign>>(`/accounts/${accountId}/get_campaigns`, {
        ...filters,
        ...restParams,
    }).then(response => response.data);
};

const fetchCampaignsByActiveStatus = (accountId: number, params: FetchCampaignsParams = {}) => {
    const { filters, ...restParams } = params;
    return Axios.post<PaginatedData<Campaign>>(
        `/accounts/${accountId}/campaigns/by-active-status`,
        {
            ...filters,
            ...restParams,
        },
    ).then(response => response.data);
};

const fetchCampaign = ({ campaignId }: { campaignId: number }) =>
    Axios.get<Campaign>(`/campaigns/${campaignId}`).then(response => response.data);

const duplicateCampaign = (campaignId: number) =>
    Axios.post<Campaign>(`/campaigns/${campaignId}/duplicate`).then(response => response.data);

const deleteCampaign = (campaignId: number) =>
    Axios.delete<Campaign>(`/campaigns/${campaignId}`).then(response => response.data);

const updateCampaign = <TData>({ campaignId, data }: UpdateCampaignParams<TData>) =>
    Axios.patch(`/campaigns/${campaignId}`, data).then(response => response.data);

const createCampaignMatch = (campaignId: number, params: CampaignMatchRequestParams) =>
    Axios.post<CampaignMatch>(`/campaigns/${campaignId}/matches`, params);

const updateCampaignMatch = (
    campaignId: number,
    matchId: number,
    params: CampaignMatchRequestParams,
) => Axios.patch<CampaignMatch>(`/campaigns/${campaignId}/matches/${matchId}`, params);

const pinCampaign = (campaignId: number) =>
    Axios.post(`/campaigns/${campaignId}/pin`).then(response => response.data);

const unpinCampaign = (campaignId: number) =>
    Axios.post(`/campaigns/${campaignId}/unpin`).then(response => response.data);

// CAMPAIGN RESOURCES
interface CreateTeamParams extends Pick<Team, 'name'> {
    goal: number | null;
    logo: string | null;
    slug: string | null;
}

interface CreateMemberParams extends Pick<Member, 'display_name'> {
    goal: number | null;
    picture: string | null;
    team_id: number | null;
}

type EditMemberParams = Omit<CreateMemberParams, 'picture'> & {
    description: string | null;
    picture?: Pick<CreateMemberParams, 'picture'>;
};

const fetchCampaignTeams = (campaignId: number, params: FetchCampaignResourceParams = {}) =>
    Axios.get<PaginatedData<Team>>(`/campaigns/${campaignId}/teams`, {
        params,
    }).then(response => response.data);

const fetchCampaignTeamById = (campaignId: number, teamId: number) =>
    Axios.get<Team>(`/campaigns/${campaignId}/teams/${teamId}`).then(response => response.data);

const createCampaignTeam = (campaignId: number, team: CreateTeamParams) =>
    Axios.post<Team>(`/campaigns/${campaignId}/teams`, team).then(response => response.data);

const updateCampaignTeam = (campaignId: number, teamId: number, team: CreateTeamParams) =>
    Axios.patch<Team>(`/campaigns/${campaignId}/teams/${teamId}`, team).then(
        response => response.data,
    );

const deleteCampaignTeam = (campaignId: number, teamId: number) =>
    Axios.delete(`/campaigns/${campaignId}/teams/${teamId}`);

const fetchCampaignMembers = (campaignId: number, params: FetchCampaignResourceParams = {}) =>
    Axios.get<PaginatedData<Member>>(`/campaigns/${campaignId}/members`, {
        params,
    }).then(response => response.data);

const fetchCampaignMemberById = (campaignId: number, memberId: number) =>
    Axios.get<Member>(`/campaigns/${campaignId}/members/${memberId}`).then(
        response => response.data,
    );

const createCampaignMember = (campaignId: number, member: CreateMemberParams) =>
    Axios.post<Member>(`/campaigns/${campaignId}/members`, member).then(response => response.data);

const updateCampaignMember = (campaignId: number, memberId: number, member: EditMemberParams) =>
    Axios.patch<Member>(`/campaigns/${campaignId}/members/${memberId}`, member).then(
        response => response.data,
    );

const deleteCampaignMember = (campaignId: number, memberId: number) =>
    Axios.delete(`/campaigns/${campaignId}/members/${memberId}`);

const inviteCampaignMemberByEmail = (campaignId: number, emails: string[]) =>
    Axios.post<PaginatedData<Member>>(`/campaigns/${campaignId}/invites`, {
        emails,
    }).then(response => response.data);

// ITEMS
const fetchCampaignItems = <T = CampaignItem>({
    campaignId,
    ...params
}: FetchCampaignItemsParams) =>
    Axios.get<PaginatedData<T>>(`/campaigns/${campaignId}/items`, {
        params,
    }).then(response => response.data);

const fetchCampaignItem = <T = CampaignItem>({
    campaignId,
    itemId,
    ...params
}: FetchCampaignItemParams) =>
    Axios.get<T>(`/campaigns/${campaignId}/items/${itemId}`, { params }).then(
        response => response.data,
    );

const createCampaignItem = ({ campaignId, data, ...params }: CreateCampaignItemParams) =>
    Axios.post<AuctionItem>(`/campaigns/${campaignId}/items`, data, { params }).then(
        response => response.data,
    );

const updateCampaignItem = ({ campaignId, itemId, data, ...params }: UpdateCampaignItemParams) =>
    Axios.patch<AuctionItem>(`/campaigns/${campaignId}/items/${itemId}`, data, { params }).then(
        response => response.data,
    );

const extendCampaignItem = ({ campaignId, itemId, ...data }: ExtendCampaignItemParams) =>
    Axios.post<AuctionItem>(`/campaigns/${campaignId}/items/${itemId}/extend`, data).then(
        response => response.data,
    );

const cancelCampaignItem = ({ campaignId, itemId }: CancelCampaignItemParams) =>
    Axios.post(`/campaigns/${campaignId}/items/${itemId}/cancel`).then(response => response.data);

const deleteCampaignItem = ({ campaignId, itemId }: DeleteCampaignItemParams) =>
    Axios.delete(`/campaigns/${campaignId}/items/${itemId}`).then(response => response.data);

const pauseCampaignItemBidding = (campaignId: number, itemId: number) =>
    Axios.post(`/campaigns/${campaignId}/items/${itemId}/pause`).then(response => response.data);

const resumeCampaignItemBidding = (campaignId: number, itemId: number) =>
    Axios.post(`/campaigns/${campaignId}/items/${itemId}/resume`).then(response => response.data);

const reorderCampaignItems = (campaignId: number, data: ReorderCampaignItemsParams) =>
    Axios.post(`/campaigns/${campaignId}/items/reorder`, data).then(response => response.data);

// PROMO CODES

const fetchCampaignPromoCodes = (campaignId: number, params: FetchCampaignPromoCodesParams = {}) =>
    Axios.get<PaginatedData<PromoCode>>(`/campaigns/${campaignId}/discount_codes`, { params }).then(
        response => response.data,
    );

const fetchCampaignPromoCode = (campaignId: number, promoCodeId: number) =>
    Axios.get<PromoCode>(`/campaigns/${campaignId}/discount_codes/${promoCodeId}`).then(
        response => response.data,
    );

const createCampaignPromoCode = (campaignId: number, data: PromoCodeRequest) =>
    Axios.post(`/campaigns/${campaignId}/discount_codes`, data).then(response => response.data);

const updateCampaignPromoCode = (campaignId: number, promoCodeId: number, data: PromoCodeRequest) =>
    Axios.patch(`/campaigns/${campaignId}/discount_codes/${promoCodeId}`, data).then(
        response => response.data,
    );

const deleteCampaignPromoCode = (campaignId: number, promoCodeId: number) =>
    Axios.delete(`/campaigns/${campaignId}/discount_codes/${promoCodeId}`).then(
        response => response.data,
    );

// AUCTIONS
const fetchCampaignAuction = ({ campaignId, auctionId }: FetchAuctionParams) =>
    Axios.get<Auction>(`/campaigns/${campaignId}/auctions/${auctionId}`).then(
        response => response.data,
    );

const createCampaignAuction = <T>({ campaignId, data }: CreateAuctionParams<T>) =>
    Axios.post(`/campaigns/${campaignId}/auctions`, data).then(response => response.data);

const updateCampaignAuction = <T>({ campaignId, auctionId, data }: UpdateAuctionParams<T>) =>
    Axios.patch(`/campaigns/${campaignId}/auctions/${auctionId}`, data).then(
        response => response.data,
    );

const deleteCampaignAuction = ({ campaignId, auctionId }: DeleteAuctionParams) =>
    Axios.delete(`/campaigns/${campaignId}/auctions/${auctionId}`).then(response => response.data);

const cancelCampaignAuction = ({ campaignId, auctionId }: CancelAuctionParams) =>
    Axios.post(`/campaigns/${campaignId}/auctions/${auctionId}/cancel`).then(
        response => response.data,
    );

// BIDS
const fetchCampaignItemBids = ({ campaignId, itemId, ...params }: FetchCampaignItemBidsParams) =>
    Axios.get<PaginatedData<Bid>>(`/campaigns/${campaignId}/items/${itemId}/bids`, { params }).then(
        response => response.data,
    );

const fetchAuctionBids = ({ campaignId, auctionId, ...params }: FetchAuctionBidsParams) =>
    Axios.get<PaginatedData<Bid>>(`/campaigns/${campaignId}/auctions/${auctionId}/bids`, {
        params,
    }).then(response => response.data);

const fetchAuctionCategories = (
    campaignId: number,
    auctionId: number,
    params: { per_page: number } = { per_page: 999 },
) =>
    Axios.get<PaginatedData<AuctionCategory>>(
        `/campaigns/${campaignId}/auctions/${auctionId}/categories`,
        {
            params,
        },
    ).then(response => response.data);

const createAuctionCategory = (
    campaignId: number,
    auctionId: number,
    category: Partial<AuctionCategory>,
) =>
    Axios.post(`/campaigns/${campaignId}/auctions/${auctionId}/categories`, category).then(
        response => response.data,
    );

const reorderAuctionCategories = (campaignId: number, auctionId: number, order: number[]) =>
    Axios.post(`/campaigns/${campaignId}/auctions/${auctionId}/categories/reorder`, { order }).then(
        response => response.data,
    );

const updateAuctionCategory = (
    campaignId: number,
    auctionId: number,
    categoryId: number,
    category: Partial<AuctionCategory>,
) =>
    Axios.patch(
        `/campaigns/${campaignId}/auctions/${auctionId}/categories/${categoryId}`,
        category,
    ).then(response => response.data);

const deleteAuctionCategory = (campaignId: number, auctionId: number, categoryId: number) =>
    Axios.delete(`/campaigns/${campaignId}/auctions/${auctionId}/categories/${categoryId}`).then(
        response => response.data,
    );

const markUncollectable = (
    campaignId: number,
    auctionId: number,
    bidId: number,
    params?: MarkUncollectableParams,
) =>
    Axios.post<Bid>(`/campaigns/${campaignId}/auctions/${auctionId}/bids/${bidId}/disqualify`, {
        second_chance_bid_id: params?.secondChanceBidId,
    }).then(response => response.data);

const chargeBidder = (campaignId: number, auctionId: number, bidId: number) =>
    Axios.post<Bid>(`/campaigns/${campaignId}/auctions/${auctionId}/bids/${bidId}/charge`).then(
        response => response.data,
    );

const createItemBid = (campaignId: number, itemId: number, body: CreateCampaignItemBidBody) =>
    Axios.post<Bid>(`/campaigns/${campaignId}/items/${itemId}/bids`, body).then(
        response => response.data,
    );

// BIDDERS
const fetchAuctionBidders = (
    campaignId: number,
    auctionId: number,
    params: FetchAuctionBiddersParams = {},
) =>
    Axios.get<PaginatedData<Bidder>>(`/campaigns/${campaignId}/auctions/${auctionId}/bidders`, {
        params,
    }).then(response => response.data);

const fetchAuctionBidder = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.get<Bidder>(`/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}`).then(
        response => response.data,
    );

interface AuctionBidderParams {
    first_name: string;
    middle_name: string;
    last_name: string;
    email: string;
    phone?: string;
    number?: number;
    email_subscription: boolean;
    phone_subscription: boolean;
    address_subscription: boolean;
    shipping_address?: Partial<Address>;
    anonymous: boolean;
    request_bidder_payment_method: boolean;
}

const createAuctionBidder = (campaignId: number, auctionId: number, params: AuctionBidderParams) =>
    Axios.post(`/campaigns/${campaignId}/auctions/${auctionId}/bidders`, {
        ...params,
        acknowledged_terms: true,
    }).then(response => response.data);

const invoiceBidder = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.post<Bidder>(
        `/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}/invoice`,
    ).then(response => response.data);

const updateAuctionBidder = (
    campaignId: number,
    auctionId: number,
    bidderId: number,
    data: Partial<AuctionBidderParams> = {},
) =>
    Axios.patch<Bidder>(
        `/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}`,
        data,
    ).then(response => response.data);

const banAuctionBidder = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.post<Bidder>(
        `/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}/ban`,
    ).then(response => response.data);

const unbanAuctionBidder = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.post<Bidder>(
        `/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}/unban`,
    ).then(response => response.data);

const deleteAuctionBidder = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.delete<Bidder>(`/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}`).then(
        response => response.data,
    );

const requestBidderPaymentMethod = (campaignId: number, auctionId: number, bidderId: number) =>
    Axios.post<Bidder>(
        `/campaigns/${campaignId}/auctions/${auctionId}/bidders/${bidderId}/request-payment-method`,
    ).then(response => response.data);

interface BidderPaymentPreviewParam {
    amount: number;
    action: BidderInvoiceType;
}

const fetchBidderPaymentPreview = (
    campaignId: number,
    itemId: number,
    data: BidderPaymentPreviewParam,
) =>
    Axios.post<BidderPaymentPreview>(
        `/campaigns/${campaignId}/items/${itemId}/declare-winner/preview`,
        data,
    ).then(response => response.data);

interface ProcessBidderPaymentParam {
    bidder_id?: number;
    amount: number;
    action: BidderInvoiceType;
    payment_method_id?: number;
    address_id?: number;
    offline_payment_data?: {
        method: string;
        check_number?: string | null;
        check_deposited_at?: string | null;
    };
}

const processBidderPayment = (
    campaignId: number,
    itemId: number,
    data: ProcessBidderPaymentParam,
) =>
    Axios.post<Transaction>(`/campaigns/${campaignId}/items/${itemId}/declare-winner`, data).then(
        response => response.data,
    );

const fetchSponsors = (accountId: number, { page = 1 }: FilterParams) =>
    Axios.get<PaginatedData<Sponsor>>(`/accounts/${accountId}/sponsors`, {
        params: { page },
    }).then(response => response.data);

const fetchTemplates = (
    accountId: number,
    sponsorId: number,
    { page = 1, ...params }: FilterParams,
) =>
    Axios.get(`/accounts/${accountId}/sponsors/search`, {
        params: { page, sponsor: sponsorId, ...params },
    }).then(response => response.data);

const acceptSponsorTerms = (accountId: number, sponsorId: number) =>
    Axios.post(`/accounts/${accountId}/sponsors/${sponsorId}/accept_terms`).then(
        response => response.data,
    );

const fetchSponsorTemplateById = (accountId: number, sponsorId: number, templateId: number) =>
    Axios.get<{ data: SponsorItem }>(
        `/accounts/${accountId}/sponsors/${sponsorId}/items/${templateId}`,
    ).then(response => response.data.data);

const sendTestCampaignEmail = (campaignId: number) =>
    Axios.post(`/campaigns/${campaignId}/actions?action=send_test_email`).then(
        response => response.data,
    );

const fetchCampaignExperts = (campaignId: number, params: FetchCampaignExpertsParams = {}) =>
    Axios.get<CampaignExpert[]>(`/campaigns/${campaignId}/experts`, {
        params: { available: params?.isAvailable },
    }).then(response => response.data);

const deleteCampaignExpertById = (campaignId: number, expertId: number) =>
    Axios.delete(`/campaigns/${campaignId}/experts/${expertId}`).then(response => response.data);

const linkExpertToCampaign = (campaignId: number, expertId: number) =>
    Axios.post(`/campaigns/${campaignId}/experts`, {
        partner_id: expertId,
    }).then(response => response.data);

const fetchCampaignInvites = (campaignId: number, params: FetchCampaignInvitesParams = {}) =>
    Axios.get<PaginatedData<CampaignMemberInvite>>(`/campaigns/${campaignId}/invites`, {
        params,
    }).then(response => response.data);

const sendCampaignInviteReminders = (campaignId: number) =>
    Axios.post(`/campaigns/${campaignId}/invites/remind`, {
        type: 'campaigns',
    }).then(response => response.data);

const sendCampaignInviteReminderById = (campaignId: number, inviteId: string) =>
    Axios.post(`/campaigns/${campaignId}/invites/${inviteId}/remind`, {
        type: 'campaigns',
    }).then(response => response.data);

const deleteCampaignInviteById = (campaignId: number, inviteId: string) =>
    Axios.delete(`/campaigns/${campaignId}/invites/${inviteId}`).then(response => response.data);

const fetchCampaignCustomFields = (campaignId: number, params: { group?: CustomFieldGroup } = {}) =>
    Axios.get<CustomField[]>(`/campaigns/${campaignId}/custom_fields`, {
        params,
    }).then(response => response.data);

// CUSTOM FIELDS

const archiveCustomField = (campaignId: number, id: number) =>
    Axios.post(`/campaigns/${campaignId}/custom_fields/${id}/archive`).then(
        response => response.data,
    );

const unarchiveCustomField = (campaignId: number, id: number) =>
    Axios.post(`/campaigns/${campaignId}/custom_fields/${id}/unarchive`).then(
        response => response.data,
    );

const deleteCustomField = (campaignId: number, id: number) =>
    Axios.delete(`/campaigns/${campaignId}/custom_fields/${id}`).then(response => response.data);

const reorderCustomField = (campaignId: number, order: string) =>
    Axios.post(`/campaigns/${campaignId}/custom_fields/reorder`, { order }).then(
        response => response.data,
    );

const createCustomField = (campaignId: number, form: CampaignCustomField) =>
    Axios.post(`/campaigns/${campaignId}/custom_fields`, form).then(response => response.data);

const updateCustomField = (campaignId: number, fieldId: number, field: CampaignCustomField) =>
    Axios.patch(`/campaigns/${campaignId}/custom_fields/${fieldId}`, field).then(
        response => response.data,
    );

export {
    fetchSponsors,
    fetchSponsorTemplateById,
    fetchTemplates,
    acceptSponsorTerms,
    sendTestCampaignEmail,
    //
    createCampaign,
    fetchCampaigns,
    fetchCampaignsByActiveStatus,
    fetchCampaign,
    updateCampaign,
    duplicateCampaign,
    deleteCampaign,
    //
    createCampaignMatch,
    updateCampaignMatch,
    //
    pinCampaign,
    unpinCampaign,
    //
    fetchCampaignTeams,
    fetchCampaignTeamById,
    createCampaignTeam,
    updateCampaignTeam,
    deleteCampaignTeam,
    //
    fetchCampaignMembers,
    fetchCampaignMemberById,
    createCampaignMember,
    updateCampaignMember,
    deleteCampaignMember,
    inviteCampaignMemberByEmail,
    //
    fetchCampaignItems,
    fetchCampaignItem,
    createCampaignItem,
    updateCampaignItem,
    cancelCampaignItem,
    deleteCampaignItem,
    reorderCampaignItems,
    pauseCampaignItemBidding,
    resumeCampaignItemBidding,
    extendCampaignItem,
    //
    fetchCampaignPromoCodes,
    fetchCampaignPromoCode,
    createCampaignPromoCode,
    updateCampaignPromoCode,
    deleteCampaignPromoCode,
    //
    fetchCampaignAuction,
    createCampaignAuction,
    updateCampaignAuction,
    deleteCampaignAuction,
    cancelCampaignAuction,
    //
    createItemBid,
    fetchCampaignItemBids,
    fetchAuctionBids,
    markUncollectable,
    chargeBidder,
    //
    createAuctionBidder,
    fetchAuctionBidder,
    fetchAuctionBidders,
    updateAuctionBidder,
    banAuctionBidder,
    unbanAuctionBidder,
    invoiceBidder,
    fetchBidderPaymentPreview,
    processBidderPayment,
    deleteAuctionBidder,
    requestBidderPaymentMethod,
    createAuctionCategory,
    fetchAuctionCategories,
    reorderAuctionCategories,
    updateAuctionCategory,
    deleteAuctionCategory,
    //
    fetchCampaignExperts,
    deleteCampaignExpertById,
    linkExpertToCampaign,
    //
    fetchCampaignInvites,
    deleteCampaignInviteById,
    sendCampaignInviteReminders,
    sendCampaignInviteReminderById,
    //
    fetchCampaignCustomFields,
    //
    archiveCustomField,
    unarchiveCustomField,
    deleteCustomField,
    reorderCustomField,
    createCustomField,
    updateCustomField,
};

export type {
    MediaUpload,
    FetchCampaignsParams,
    FetchCampaignResourceParams,
    UpdateCampaignParams,
    CampaignMatchRequestParams,
    CreateAuctionParams,
    UpdateAuctionParams,
    ItemRequest,
    TicketBundles,
    CustomFieldRequest,
    PromoCodeRequest,
    FetchCampaignItemsParams,
    FetchCampaignItemParams,
    CreateCampaignItemParams,
    UpdateCampaignItemParams,
    DeleteCampaignItemParams,
    ExtendCampaignItemParams,
    CampaignItemBundleItemRequest,
    BidStatusFilter,
    FetchCampaignExpertsParams,
    FetchCampaignInvitesParams,
    CreateTeamParams,
    FetchCampaignPromoCodesParams,
    FetchAuctionBiddersParams,
    FetchAuctionBidsParams,
    FetchAuctionParams,
    FetchCampaignItemBidsParams,
};
